import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/store/auth.store'

// Instância base do Axios — todos os serviços passam por aqui
export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ── Sessão (RCA 2026-10-22) ──────────────────────────────────────
// Mesmas garantias do app mobile:
//  1. refresh single-flight (interceptor e sockets compartilham UMA requisição);
//  2. geração de sessão: respostas de uma sessão anterior nunca encerram a atual;
//  3. só encerra a sessão quando o SERVIDOR rejeita o refresh (rede/5xx não deslogam);
//  4. 401 de rotas de autenticação (senha errada) e de requisições sem token não é "sessão expirada".
let generation = 0

export const currentGeneration = (): number => generation
export const bumpSession = (): number => ++generation

// Login/logout (troca de identidade) = nova sessão lógica. Guard para ambientes de teste com store simulada.
useAuthStore.subscribe?.((state, prev) => {
  if (state.user?.id !== prev.user?.id) bumpSession()
})

export type RefreshOutcome =
  | { ok: true; accessToken: string }
  | { ok: false; reason: 'expired' | 'network' | 'stale' }

const endSession = (fromGeneration: number) => {
  if (fromGeneration !== generation) return
  bumpSession()
  useAuthStore.getState().logout()
  // Redireciona com flag para exibir mensagem amigável no Login (uma única vez por sessão)
  if (window.location.pathname !== '/login') window.location.href = '/login?expired=1'
}

let inflight: Promise<RefreshOutcome> | null = null

const doRefresh = async (gen: number): Promise<RefreshOutcome> => {
  try {
    // Refresh via cookie httpOnly — não envia body, cookie vai automaticamente
    const { data } = await axios.post('/api/auth/refresh', {}, { withCredentials: true })
    if (gen !== generation) return { ok: false, reason: 'stale' }
    useAuthStore.getState().setTokens(data.accessToken)
    return { ok: true, accessToken: data.accessToken }
  } catch (err) {
    if (gen !== generation) return { ok: false, reason: 'stale' }
    const status = (err as { response?: { status?: number } }).response?.status
    if (status === 401 || status === 403) {
      endSession(gen)
      return { ok: false, reason: 'expired' }
    }
    return { ok: false, reason: 'network' }
  }
}

/** Renova a sessão. Chamadas concorrentes compartilham a mesma promise. */
export const refreshSession = (): Promise<RefreshOutcome> => {
  if (inflight) return inflight
  inflight = doRefresh(generation).finally(() => {
    inflight = null
  })
  return inflight
}

interface SessionMeta {
  __gen?: number
  __authed?: boolean
  _retry?: boolean
}

// ── Request Interceptor: adiciona JWT Bearer e Correlation ID ────
api.interceptors.request.use((config: InternalAxiosRequestConfig & SessionMeta) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.__gen = generation
  config.__authed = !!token
  config.headers['X-Correlation-ID'] = crypto.randomUUID()
  return config
})

const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password', '/auth/verify-email']

// ── Response Interceptor: refresh silencioso do access token ─────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & SessionMeta) | undefined

    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      // 403 — token válido mas sem permissão: não redireciona, deixa o componente tratar
      return Promise.reject(error)
    }
    // Senha errada / token de e-mail inválido etc.: resposta normal, não é sessão expirada
    if (AUTH_PATHS.some((p) => originalRequest.url?.includes(p))) return Promise.reject(error)
    // Requisição sem token ou de uma sessão que já foi trocada/encerrada: não mexe na sessão atual
    if (!originalRequest.__authed || originalRequest.__gen !== generation) return Promise.reject(error)

    originalRequest._retry = true
    const out = await refreshSession()

    if (out.ok) {
      originalRequest.headers.Authorization = `Bearer ${out.accessToken}`
      originalRequest.__gen = generation
      return api(originalRequest)
    }
    // refreshSession já encerrou a sessão quando o servidor rejeitou o cookie de refresh
    return Promise.reject(error)
  }
)
